import { createCalendar } from "@internationalized/date";
import { type DateValue, useRangeCalendar } from "@react-aria/calendar";
import { useLocale } from "@react-aria/i18n";
import { type RangeCalendarStateOptions, useRangeCalendarState } from "@react-stately/calendar";
import { type FC, useRef } from "react";
import { type AriaRangeCalendarProps } from "react-aria";

import { CalendarGrid } from "./CalendarGrid";
import { CalendarHeader } from "./CalendarHeader";

export type RangeCalendarProps = Omit<
	RangeCalendarStateOptions<DateValue> & AriaRangeCalendarProps<DateValue>,
	"locale" | "createCalendar"
> & { visibleDurationMonths?: 1 | 2 };

export const RangeCalendar: FC<RangeCalendarProps> = ({ visibleDurationMonths = 1, ...props }) => {
	const { locale } = useLocale();
	const state = useRangeCalendarState({
		...props,
		visibleDuration: { months: visibleDurationMonths },
		locale,
		createCalendar,
	});

	const ref = useRef<HTMLDivElement>(null);
	const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);

	return (
		<div {...calendarProps} ref={ref} className="inline-block text-primary">
			<CalendarHeader
				state={state}
				calendarProps={calendarProps}
				prevButtonProps={prevButtonProps}
				nextButtonProps={nextButtonProps}
				visibleDurationMonths={visibleDurationMonths}
			/>
			<div className="flex items-start gap-8">
				<CalendarGrid state={state} />
				{visibleDurationMonths > 1 && <CalendarGrid state={state} offset={{ months: 1 }} />}
				{visibleDurationMonths > 2 && <CalendarGrid state={state} offset={{ months: 2 }} />}
			</div>
		</div>
	);
};
